/*
  Значок «i» с пояснением — приём перенесён из Арбитр ДА (`InfoTooltip.tsx`,
  заведён там 19.08.2026) и переписан под здешние классы: тут свои стили в
  `styles.css`, а не утилитарные.

  Зачем он здесь. Каждую цифру на экране отчётов можно оспорить, и §4.5.2
  требует держать рядом объяснение, откуда она взялась. Но объяснения длиной в
  две-три строки под каждой плиткой делали её высотой втрое больше самой цифры,
  и экран превращался в текст, среди которого потерялись деньги (08.09.2026,
  замечание владельца). Пояснение уехало под значок в правом верхнем углу
  плитки, а сама цифра осталась главной.

  **Панель - `position: fixed`**, а не блок внутри плитки. Плитка обрезала бы
  панель по своей границе, а соседние карточки перекрыли бы её.

  Где именно она открывается - `hintPlacement.js`: сверху справа, не помещается
  - сверху слева, потом снизу (02.10.2026, указание владельца). Решение
  принимается по НАСТОЯЩЕМУ размеру панели, поэтому показ идёт в два шага:
  панель рисуется невидимой в углу окна, замеряется и только потом встаёт на
  место. Пока она открыта, прокрутка и смена размера окна двигают её вместе со
  значком - иначе панель висела бы там, где значка уже нет.

  Показывается и по наведению, и по фокусу с клавиатуры: `tabIndex` на значке
  стоит именно для этого.
*/

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { placeHint } from './hintPlacement.js'

// Первый шаг показа: панель есть, но не видна, пока её не замерили.
const MEASURING = { position: 'fixed', left: 0, top: 0, visibility: 'hidden' }

export default function InfoHint({ text, title = 'Что это' }) {
  const [open, setOpen] = useState(false)
  const [spot, setSpot] = useState(null)
  const iconRef = useRef(null)
  const panelRef = useRef(null)

  useLayoutEffect(() => {
    if (!open) return undefined
    const update = () => {
      const icon = iconRef.current
      const panel = panelRef.current
      if (!icon || !panel) return
      const view = { width: window.innerWidth, height: window.innerHeight }
      setSpot(placeHint(icon.getBoundingClientRect(), panel.getBoundingClientRect(), view))
    }
    update()
    // `capture`: прокручивается не только окно, но и рабочая зона кабинета и
    // окна поверх страницы, а их прокрутка до окна не всплывает.
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, text])

  const show = () => setOpen(true)
  const hide = useCallback(() => {
    setOpen(false)
    setSpot(null)
  }, [])

  /*
    Esc закрывает подсказку (аудит 28.10.2026, раздел 5.4; правило WCAG 1.4.13): раньше её
    можно было убрать только уводом указателя или фокуса, а панель закрывала собой цифры под
    ней. Слушатель стоит на окне в фазе перехвата: Esc работает и при показе по наведению
    (фокуса на значке нет), а подсказка внутри окна закрывается первой - Esc помечается
    обработанным, и окно вокруг неё остаётся.
  */
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      hide()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, hide])

  return (
    <span
      ref={iconRef}
      className="hint"
      tabIndex={0}
      /*
        Роли «кнопка» здесь нет намеренно, и это не про доступность, а про
        раскладку: правило подъёма в `styles.css` действует на всё с
        `role="button"`, а `transform` у предка превращает его в систему
        отсчёта для `position: fixed` - панель уезжала в угол экрана
        (08.09.2026, найдено осмотром). Читалке значок называет `aria-label`,
        а нажимать его не нужно: он раскрывается по наведению и по фокусу.
      */
      aria-label={`${title}: ${text}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      i
      {open ? (
        <span
          ref={panelRef}
          className="hint__panel"
          style={spot ? { position: 'fixed', left: spot.left, top: spot.top } : MEASURING}
        >
          <span className="hint__title">{title}</span>
          <span className="hint__text">{text}</span>
        </span>
      ) : null}
    </span>
  )
}
